import { Event } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { ServiceCountry } from '@mirai/services';
import { Button, Form, Input, Notification, Progress, Select, styles, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { SCRIPT_ID, SCRIPT_URL } from './FormHotelpay.constants';
import { L10N } from './FormHotelpay.l10n';
import * as style from './FormHotelpay.module.css';
import { getStyle } from './helpers';
import { loadScript } from '../../../../../helpers/loadScript';
import { trackError } from '../../../../../helpers/trackError';
import { EVENT } from '../../../../helpers';

export const FormHotelpay = ({ publicKey, response, onClose = () => {}, ...others }) => {
  const { language, locale, translate } = useLocale();

  const [busy, setBusy] = useState(false);
  const [countries, setCountries] = useState({});
  const [error, setError] = useState();
  const [form, setForm] = useState({});
  const [paymentReady, setPaymentReady] = useState();
  const [scriptReady, setScriptReady] = useState(false);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    let callback;

    (async () => {
      callback = await loadScript({ domain: 'payments', id: SCRIPT_ID, src: SCRIPT_URL, onLoad: handleLoad });
    })();

    return () => {
      callback && callback();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const next = (await ServiceCountry.list(locale)) || {};
      const [, country] = locale.split('-');

      setCountries(next);
      setForm({ ...form, country });
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (!scriptReady || !response) return;

    try {
      const { Checkout } = window.Hotelpay;
      const { configuration: { amount, currency_code, customer_info, items, reserve_data } = {} } = response;

      Checkout.setApiKey(publicKey);
      Checkout.setMode('production');
      Checkout.setLanguage(language);
      Checkout.setCurrency(currency_code);
      Checkout.setTotal(amount);
      Checkout.setDetails({ customer_info, items, reserve_data });
      Checkout.setCSS(getStyle());

      Checkout.on('complete-form-card', ({ is_valid } = {}) => {
        setValid(is_valid);
      });

      Checkout.on('transaction-completed', handleTransactionCompleted);

      Checkout.on('transaction-failed', (error) => {
        setBusy(false);
        if (error) trackError('Hotelpay transaction failed', error instanceof Error ? error.stack : error?.message);

        Event.publish(EVENT.NOTIFICATION, {
          error: true,
          defaultMessage: translate(L10N.NOTIFICATION_ERROR),
        });
      });

      Checkout.renderIframe('#payment-form');

      setPaymentReady(true);
    } catch (error) {
      if (error) trackError('Hotelpay loading error', error instanceof Error ? error.stack : error?.message);

      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptReady, response]);

  const handleLoad = () => {
    setScriptReady(true);
  };

  const handlePayment = async () => {
    const { Checkout } = window.Hotelpay || {};
    const { country, ...props } = form;

    Checkout.setAddress({ ...props, country: countries[country], country_code: country });
    setBusy(true);

    try {
      await Checkout.processPayment();
    } catch {
      setBusy(false);

      Event.publish(EVENT.NOTIFICATION, {
        error: true,
        defaultMessage: translate(L10N.NOTIFICATION_ERROR),
      });
    }
  };

  const handleTransactionCompleted = ({ confirmationCode, orderId, pspReference, transactionId } = {}) => {
    onClose();
    const { configuration: { returnUrl } = {} } = response || {};

    const url = new URL(returnUrl);
    confirmationCode && url.searchParams.append('confirmationCode', confirmationCode);
    orderId && url.searchParams.append('orderId', orderId);
    pspReference && url.searchParams.append('pspReference', pspReference);
    transactionId && url.searchParams.append('transactionId', transactionId);

    window.location.href = url.toString();
  };

  return (
    <>
      <View
        {...others}
        id="payment-form"
        className={styles(others.className, style.iframe, paymentReady && style.ready)}
      >
        <Progress indeterminate visible={!paymentReady && !error} />
        {error && (
          <Notification error large wide>
            {translate(L10N.NOTIFICATION_ERROR)}
          </Notification>
        )}
      </View>
      {paymentReady && (
        <Form onChange={setForm} className={style.form}>
          <Text action medium label-for="country">
            {translate(L10N.LABEL_COUNTRY)}
            <Text action className={style.required}>
              *
            </Text>
          </Text>
          <Select
            options={Object.values(countries)}
            values={Object.keys(countries)}
            name="country"
            value={form.country}
          />
          <Text action medium label-for="state">
            {translate(L10N.LABEL_STATE)}
            <Text action className={style.required}>
              *
            </Text>
          </Text>
          <Input name="state" value={form.state} />
          <Text action medium label-for="city">
            {translate(L10N.LABEL_CITY)}
            <Text action className={style.required}>
              *
            </Text>
          </Text>
          <Input name="city" value={form.city} />
          <Text action medium label-for="address">
            {translate(L10N.LABEL_ADDRESS)}
            <Text action className={style.required}>
              *
            </Text>
          </Text>
          <Input name="address" value={form.address} />
          <Text action medium label-for="zip">
            {translate(L10N.LABEL_ZIP)}
            <Text action className={style.required}>
              *
            </Text>
          </Text>
          <Input name="zip" value={form.zip} />
        </Form>
      )}

      <Button
        busy={busy}
        disabled={!valid || Object.values(form).some((value) => !value)}
        wide
        onPress={handlePayment}
        className={style.button}
      >
        {translate(L10N.ACTION_PAY)}
      </Button>
    </>
  );
};

FormHotelpay.displayName = 'Mirai:Payments:ButtonPayment:FormHotelpay';

FormHotelpay.propTypes = {
  publicKey: PropTypes.string,
  response: PropTypes.object,
  onClose: PropTypes.func,
};
